import type { CastRow, PropRow } from "@/components/prompt-editor/mention-sources"
import {
  characterSizeFromHeightCm,
  DEFAULT_CHARACTER_HEIGHT_CM,
  DEFAULT_PROP_HEIGHT_CM,
  FLOOR_EXTENT,
  paletteColorFor,
  propSizeFromHeightCm,
} from "@/lib/location-scene-3d/palette"
import {
  CAMERA_PATH_VERSION,
  emptyCameraPath,
  lookTimeMs,
  normalizeLookKeys,
  type CameraPath,
} from "@/lib/location-scene-3d/camera-path"
import { normalizeCameraPathTakes, type CameraPathTake } from "@/lib/location-scene-3d/camera-path-takes"
import { normalizePathAuthoring } from "@/lib/location-scene-3d/camera-path-spline"
import { normalizeSpeedProfile } from "@/lib/location-scene-3d/camera-speed"
import {
  cloneObjectPath,
  normalizeObjectPath,
  objectPathStart,
  type ObjectPath,
} from "@/lib/location-scene-3d/object-path"
import {
  emptyPlatform,
  platformIsSet,
  sectorSizeMeters,
  sectorWorldCenter,
  walkableFloorSectors,
  type SceneCell,
  type ScenePlatform,
} from "@/lib/location-scene-3d/platform"
import type { SceneCharacterFigure } from "@/lib/location-scene-3d/character-figure"
import { defaultLightPosition } from "@/lib/location-scene-3d/platform-layer"

export type SceneObjectKind = "primitive" | "character" | "prop"
export type ScenePrimitiveShape = "box" | "sphere" | "cylinder" | "cone" | "plane"
export type SceneNavMode = "orbit" | "fly"
export type SceneGizmoMode = "translate" | "rotate" | "scale"

export type SceneObjectState = {
  uid: string
  kind: SceneObjectKind
  shape?: ScenePrimitiveShape
  entity_type?: "character" | "prop"
  entity_id?: number
  label?: string
  /** Shared id: objects with the same group_id move together. */
  group_id?: string
  color?: string
  /** Hero mesh. Absent means humanoid. Not used for props or primitives. */
  figure?: SceneCharacterFigure
  size?: [number, number, number]
  position: [number, number, number]
  rotation: [number, number, number]
  scale: [number, number, number]
  missing?: boolean
  height_explicit?: boolean
  thumb_url?: string | null
  /** Движение по точкам. Общее для всех дублей камеры. Только позиция. */
  path?: ObjectPath
}

export type SceneCameraState = {
  pos: [number, number, number]
  target?: [number, number, number]
  yaw: number
  pitch: number
  fov: number
  mode?: SceneNavMode
}

export type SceneFaceState = {
  face_key: string
  origin: "upload" | "library" | "generated"
  status: "pending" | "ready" | "failed"
  prompt?: string | null
  error_message?: string | null
  url?: string | null
  thumb_url?: string | null
  download_url?: string | null
  /** Solid fill hex from DB (`solid_color`); null/absent for image materials. */
  solid_color?: string | null
  /** Per-row wall colors (`"u:v"` → hex). Empty means `solid_color` paints the whole column. */
  tile_colors?: Record<string, string> | null
}

function tileColorsFingerprint(face: Pick<SceneFaceState, "tile_colors">): string {
  const tiles = face.tile_colors
  if (!tiles) return ""
  return Object.keys(tiles)
    .sort()
    .map((key) => `${key}=${tiles[key]}`)
    .join(",")
}

/** Stable key for one face — shared by poll fingerprint and the platform layer. */
export function sceneFaceRecordKey(face: SceneFaceState): string {
  const image = face.download_url ?? face.url ?? ""
  return [face.face_key, face.status, face.solid_color ?? "", tileColorsFingerprint(face), image].join(":")
}

/** Stable key for poll/skip — matches platform-layer facesKey. */
export function sceneFacesFingerprint(faces: ReadonlyArray<SceneFaceState> | null | undefined): string {
  if (!faces || faces.length === 0) return ""
  return [...faces]
    .sort((a, b) => a.face_key.localeCompare(b.face_key))
    .map((face) => sceneFaceRecordKey(face))
    .join("|")
}

export type SceneVersionSummary = {
  id: number
  name: string
  active: boolean
  dirty: boolean
  created_at?: string | null
}

export type SceneLayoutState = {
  exists: boolean
  objects: SceneObjectState[]
  camera_state: SceneCameraState
  /** Зафиксированный старт пролёта; null если ещё не сохраняли. */
  start_camera: SceneCameraState | null
  camera_path: CameraPath
  camera_paths?: CameraPathTake[]
  active_camera_path_id?: string | null
  platform?: { sectors_x: number; sectors_z: number; sector_size: number; unit: "cm" | "m" } | null
  cells?: { x: number; z: number; height: number }[]
  openings?: { face_key: string; u: number; v: number; depth: number }[]
  light?: SceneLightState | null
  environment?: SceneEnvironmentState | null
  faces?: SceneFaceState[]
  versions?: SceneVersionSummary[]
  active_scene_version_id?: number | null
  updated_at?: string | null
}

export type SceneLightPreset = "north" | "east" | "south" | "west" | "top" | "custom"

export type SceneLightState = {
  preset: SceneLightPreset
  position: [number, number, number]
  /** Absolute PointLight intensity; omit → engine treats as slider min (range.min). */
  intensity?: number
}

export type SceneThemeId = "light" | "dark"
export type SceneSkyMarkup = "dots" | "plus" | "none"

export type SceneEnvironmentState = {
  theme: SceneThemeId
  sky_markup: SceneSkyMarkup
}

export function defaultSceneEnvironment(): SceneEnvironmentState {
  return { theme: "light", sky_markup: "plus" }
}

const SKY_MARKUPS = new Set<SceneSkyMarkup>(["dots", "plus", "none"])

export function normalizeSceneEnvironment(
  raw: Partial<SceneEnvironmentState> | null | undefined,
): SceneEnvironmentState {
  const theme = raw?.theme === "dark" ? "dark" : "light"
  const markup = raw?.sky_markup
  const sky_markup = markup && SKY_MARKUPS.has(markup) ? markup : "none"
  return { theme, sky_markup }
}

export type SceneLayoutSummary = {
  exists: boolean
  objects_count: number
  updated_at: string | null
}

export const DEFAULT_CAMERA_STATE: SceneCameraState = {
  // Corner view over 32×32m platform (HUD: x-48.4 y50.9 z-51.3, 224°, pitch -36°, fov 30).
  pos: [-48.4, 50.9, -51.3],
  target: [0, 0.5, 0],
  yaw: (224 * Math.PI) / 180,
  pitch: (-36 * Math.PI) / 180,
  fov: 30,
  mode: "fly",
}

function isVec3(value: unknown): value is [number, number, number] {
  return Array.isArray(value) && value.length === 3 && value.every((n) => typeof n === "number" && Number.isFinite(n))
}

export function normalizeCameraState(raw: SceneCameraState | null | undefined): SceneCameraState {
  const base = DEFAULT_CAMERA_STATE
  if (!raw || typeof raw !== "object") {
    return { ...base, pos: [...base.pos], target: base.target ? [...base.target] : undefined }
  }
  return {
    pos: isVec3(raw.pos) ? raw.pos : [...base.pos],
    target: isVec3(raw.target) ? raw.target : base.target ? [...base.target] : undefined,
    yaw: typeof raw.yaw === "number" && Number.isFinite(raw.yaw) ? raw.yaw : base.yaw,
    pitch: typeof raw.pitch === "number" && Number.isFinite(raw.pitch) ? raw.pitch : base.pitch,
    fov: typeof raw.fov === "number" && Number.isFinite(raw.fov) ? raw.fov : base.fov,
    mode: "fly",
  }
}

/** Пустой `{}` с бэка = старт не задан. */
export function normalizeStartCamera(raw: SceneCameraState | null | undefined): SceneCameraState | null {
  if (!raw || typeof raw !== "object" || !isVec3(raw.pos)) return null
  return normalizeCameraState(raw)
}

export function normalizeCameraPath(raw: CameraPath | null | undefined): CameraPath {
  if (!raw || typeof raw !== "object") return emptyCameraPath()
  const samples = Array.isArray(raw.samples) ? raw.samples : []
  const authoring = normalizePathAuthoring(raw.authoring)
  const lookKeys = normalizeLookKeys(raw.look_keys)
  const duration = typeof raw.duration_ms === "number" && Number.isFinite(raw.duration_ms) ? raw.duration_ms : 0
  if (samples.length === 0 && !authoring) return emptyCameraPath()
  const version = raw.version > 0 ? raw.version : 1
  const authoringMigrated =
    version < 2 && lookKeys && authoring ? { ...authoring, look: { mode: "recorded" as const } } : authoring
  const speed = normalizeSpeedProfile((raw as { speed?: unknown }).speed, duration)
  const lookMigrated =
    version < 3 && lookKeys
      ? lookKeys.map((key) => ({
          ...key,
          t: lookTimeMs(
            {
              version,
              duration_ms: duration,
              samples: [],
              ...(speed ? { speed } : {}),
            },
            key.t,
          ),
        }))
      : lookKeys
  return {
    version: version < 3 ? CAMERA_PATH_VERSION : version,
    duration_ms: duration,
    samples,
    ...(authoringMigrated ? { authoring: authoringMigrated } : {}),
    ...(lookMigrated ? { look_keys: lookMigrated } : {}),
    ...(speed ? { speed } : {}),
  }
}

const LIGHT_PRESETS = new Set<SceneLightPreset>(["north", "east", "south", "west", "top", "custom"])

export function defaultSceneLight(span?: number): SceneLightState {
  const platform = emptyPlatform()
  const fallback = Math.max(platform.sectors_x, platform.sectors_z) * sectorSizeMeters(platform)
  const s = span != null && Number.isFinite(span) && span > 0 ? span : fallback
  return { preset: "custom", position: defaultLightPosition(s) }
}

export function normalizeSceneLight(raw: SceneLightState | null | undefined): SceneLightState {
  const fallback = defaultSceneLight()
  if (!raw || typeof raw !== "object") return fallback
  const preset = LIGHT_PRESETS.has(raw.preset) ? raw.preset : fallback.preset
  const position = isVec3(raw.position) ? raw.position : fallback.position
  const intensity =
    typeof raw.intensity === "number" && Number.isFinite(raw.intensity) && raw.intensity > 0 ? raw.intensity : undefined
  return intensity != null
    ? { preset, position: [...position] as [number, number, number], intensity }
    : { preset, position: [...position] as [number, number, number] }
}

export function normalizeSceneObject(raw: SceneObjectState): SceneObjectState {
  const path = normalizeObjectPath(raw.path)
  if (!path) {
    if (!raw.path) return raw
    const next = { ...raw }
    delete next.path
    return next
  }
  const start = objectPathStart(path)
  return {
    ...raw,
    path,
    position: start ?? raw.position,
  }
}

export function normalizeSceneLayout(raw: Partial<SceneLayoutState> | null | undefined): SceneLayoutState {
  const takes = normalizeCameraPathTakes(
    raw?.camera_paths,
    raw?.active_camera_path_id,
    normalizeCameraPath(raw?.camera_path),
    normalizeCameraPath,
  )
  return {
    exists: Boolean(raw?.exists),
    objects: Array.isArray(raw?.objects) ? raw.objects.map((obj) => normalizeSceneObject(obj)) : [],
    camera_state: normalizeCameraState(raw?.camera_state),
    start_camera: normalizeStartCamera(raw?.start_camera ?? null),
    camera_path: takes.cameraPath,
    camera_paths: takes.takes,
    active_camera_path_id: takes.activeId,
    platform: raw?.platform ?? null,
    cells: Array.isArray(raw?.cells) ? raw.cells : [],
    openings: Array.isArray(raw?.openings) ? raw.openings : [],
    light: normalizeSceneLight(raw?.light),
    environment: normalizeSceneEnvironment(raw?.environment),
    faces: Array.isArray(raw?.faces) ? raw.faces : [],
    versions: Array.isArray(raw?.versions) ? raw.versions : [],
    active_scene_version_id: raw?.active_scene_version_id ?? null,
    updated_at: raw?.updated_at ?? null,
  }
}

export function emptySceneLayout(): SceneLayoutState {
  return {
    exists: false,
    objects: [],
    camera_state: { ...DEFAULT_CAMERA_STATE },
    start_camera: null,
    camera_path: emptyCameraPath(),
    camera_paths: [],
    active_camera_path_id: null,
    updated_at: null,
  }
}

/** Previz always has a sector platform (no bare floor/grid UX). */
export function defaultPrevizLayout(): SceneLayoutState {
  return {
    ...emptySceneLayout(),
    platform: emptyPlatform(),
    cells: [],
    openings: [],
    light: defaultSceneLight(),
    environment: defaultSceneEnvironment(),
  }
}

/** Seed emptyPlatform when layout has no platform (legacy / empty edit). */
export function ensurePrevizPlatform(layout: SceneLayoutState): SceneLayoutState {
  if (platformIsSet(layout.platform)) return layout
  return {
    ...layout,
    platform: emptyPlatform(),
    cells: Array.isArray(layout.cells) ? layout.cells : [],
    openings: Array.isArray(layout.openings) ? layout.openings : [],
    light: layout.light ?? defaultSceneLight(),
  }
}

export function nextObjectUid(objects: SceneObjectState[]): string {
  const used = new Set(objects.map((o) => o.uid))
  let i = objects.length + 1
  while (used.has(`obj_${i}`)) i += 1
  return `obj_${i}`
}

export function occupiedColorsForEntity(
  objects: SceneObjectState[],
  entityType: "character" | "prop",
  entityId: number,
): string[] {
  const colors: string[] = []
  for (const obj of objects) {
    if (!obj.color) continue
    if (obj.entity_type === entityType && obj.entity_id === entityId) continue
    if (obj.kind === "character" || obj.kind === "prop") colors.push(obj.color)
  }
  return colors
}

export function existingColorForEntity(
  objects: SceneObjectState[],
  entityType: "character" | "prop",
  entityId: number,
): string | null {
  const found = objects.find((o) => o.entity_type === entityType && o.entity_id === entityId && o.color)
  return found?.color ?? null
}

export const PLACEMENT_GAP_M = 0.45
const PLACEMENT_MAX_RADIUS = FLOOR_EXTENT / 2 - 0.5

export type DropTerrain = {
  platform: ScenePlatform
  cells?: ReadonlyArray<SceneCell>
}

/** Camera-aware placement: free floor in view, else in front, else nearest to prefer. */
export type DropViewHint = {
  preferX: number
  preferZ: number
  /** Sector keys `x:z` currently in camera frustum (floor points). */
  visibleSectorKeys?: ReadonlySet<string>
  camX: number
  camZ: number
  lookX: number
  lookZ: number
}

function footprintRadius(obj: Pick<SceneObjectState, "size" | "scale">): number {
  const sx = (obj.size?.[0] ?? 1) * Math.abs(obj.scale?.[0] ?? 1)
  const sz = (obj.size?.[2] ?? obj.size?.[0] ?? 1) * Math.abs(obj.scale?.[2] ?? obj.scale?.[0] ?? 1)
  return Math.max(sx, sz) / 2
}

function roundCoord(n: number) {
  // 1 mm. A 1 cm cell center sits on a half-centimeter; rounding to 1 cm moves it into the next cell.
  return Math.round(n * 1000) / 1000
}

/**
 * Chebyshev distance (in sectors) to nearest wall (height > 0) or platform edge.
 * Higher = more open floor (middle of free space).
 */
export function sectorClearance(
  x: number,
  z: number,
  platform: Pick<ScenePlatform, "sectors_x" | "sectors_z">,
  heightByKey: ReadonlyMap<string, number>,
): number {
  const sx = platform.sectors_x
  const sz = platform.sectors_z
  let min = Infinity
  for (let oz = -1; oz <= sz; oz++) {
    for (let ox = -1; ox <= sx; ox++) {
      const outside = ox < 0 || oz < 0 || ox >= sx || oz >= sz
      const blocked = outside || (heightByKey.get(`${ox}:${oz}`) ?? 0) > 0
      if (!blocked) continue
      const d = Math.max(Math.abs(ox - x), Math.abs(oz - z))
      if (d < min) min = d
    }
  }
  return Number.isFinite(min) ? min : 0
}

type DropSpot = { x: number; z: number; key: string; d: number; clearance: number }

function compareDropSpots(a: DropSpot, b: DropSpot) {
  return b.clearance - a.clearance || a.d - b.d || a.x - b.x || a.z - b.z
}

function pickFirstFreeSpot(
  spots: DropSpot[],
  overlaps: (x: number, z: number) => boolean,
  y: number,
): [number, number, number] | null {
  const ranked = [...spots].sort(compareDropSpots)
  for (const spot of ranked) {
    if (!overlaps(spot.x, spot.z)) return [roundCoord(spot.x), y, roundCoord(spot.z)]
  }
  return null
}

export function nextDropPosition(
  objects: SceneObjectState[],
  size: [number, number, number],
  terrain?: DropTerrain | null,
  view?: DropViewHint | null,
): [number, number, number] {
  const y = size[1] / 2
  const newR = Math.max(size[0], size[2]) / 2
  const preferX = view?.preferX ?? 0
  const preferZ = view?.preferZ ?? 0
  const placementGap =
    terrain && platformIsSet(terrain.platform) ? PLACEMENT_GAP_M * sectorSizeMeters(terrain.platform) : PLACEMENT_GAP_M

  const overlaps = (x: number, z: number) =>
    objects.some((obj) => {
      const minDist = footprintRadius(obj) + newR + placementGap
      const dx = obj.position[0] - x
      const dz = obj.position[2] - z
      return dx * dx + dz * dz < minDist * minDist
    })

  const distToPrefer = (x: number, z: number) => {
    const dx = x - preferX
    const dz = z - preferZ
    return dx * dx + dz * dz
  }

  if (terrain && platformIsSet(terrain.platform)) {
    const cells = terrain.cells ?? []
    const heightByKey = new Map(cells.map((cell) => [`${cell.x}:${cell.z}`, cell.height]))
    const spots: DropSpot[] = walkableFloorSectors(terrain.platform, cells).map((sector) => {
      const [cx, cz] = sectorWorldCenter(terrain.platform, sector.x, sector.z)
      return {
        x: cx,
        z: cz,
        key: `${sector.x}:${sector.z}`,
        d: distToPrefer(cx, cz),
        clearance: sectorClearance(sector.x, sector.z, terrain.platform, heightByKey),
      }
    })

    if (view?.visibleSectorKeys && view.visibleSectorKeys.size > 0) {
      const visible = spots.filter((spot) => view.visibleSectorKeys!.has(spot.key))
      const hit = pickFirstFreeSpot(visible, overlaps, y)
      if (hit) return hit
    }

    if (view) {
      const front = spots.filter((spot) => {
        const dx = spot.x - view.camX
        const dz = spot.z - view.camZ
        return dx * view.lookX + dz * view.lookZ > 0
      })
      const hit = pickFirstFreeSpot(front, overlaps, y)
      if (hit) return hit
    }

    const hit = pickFirstFreeSpot(spots, overlaps, y)
    if (hit) return hit
    if (spots.length > 0) {
      const ranked = [...spots].sort(compareDropSpots)
      const spot = ranked[0]
      return [roundCoord(spot.x), y, roundCoord(spot.z)]
    }
  }

  const step = Math.max(newR * 2 + PLACEMENT_GAP_M, 0.8)
  if (!overlaps(preferX, preferZ)) return [roundCoord(preferX), y, roundCoord(preferZ)]

  for (let ring = 1; ring <= 16; ring++) {
    const radius = ring * step
    if (radius > PLACEMENT_MAX_RADIUS) break
    const count = ring * 6
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2
      const x = roundCoord(preferX + Math.cos(angle) * radius)
      const z = roundCoord(preferZ + Math.sin(angle) * radius)
      if (!overlaps(x, z)) return [x, y, z]
    }
  }

  return [roundCoord(preferX + Math.min(PLACEMENT_MAX_RADIUS, step * 8)), y, roundCoord(preferZ)]
}

export function buildCharacterObject(
  character: CastRow,
  objects: SceneObjectState[],
  cast: CastRow[],
  props: PropRow[],
  terrain?: DropTerrain | null,
  view?: DropViewHint | null,
): SceneObjectState {
  const existing = existingColorForEntity(objects, "character", character.id)
  const color =
    existing ??
    paletteColorFor("character", character.id, cast, props, occupiedColorsForEntity(objects, "character", character.id))
  const heightCm = character.effective_height_cm ?? DEFAULT_CHARACTER_HEIGHT_CM
  const size = characterSizeFromHeightCm(heightCm)
  return {
    uid: nextObjectUid(objects),
    kind: "character",
    shape: "cylinder",
    entity_type: "character",
    entity_id: character.id,
    label: character.name,
    color,
    figure: "humanoid",
    size,
    position: nextDropPosition(objects, size, terrain, view),
    rotation: [0, 0, 0],
    scale: [1, 1, 1],
    missing: false,
    height_explicit: Boolean(character.height_explicit),
    thumb_url: character.reference_sheet_url ?? character.portrait_thumb_url ?? null,
  }
}

export function buildPropObject(
  prop: PropRow,
  objects: SceneObjectState[],
  cast: CastRow[],
  props: PropRow[],
  terrain?: DropTerrain | null,
  view?: DropViewHint | null,
): SceneObjectState {
  const existing = existingColorForEntity(objects, "prop", prop.id)
  const color =
    existing ?? paletteColorFor("prop", prop.id, cast, props, occupiedColorsForEntity(objects, "prop", prop.id))
  const heightCm = prop.effective_height_cm ?? DEFAULT_PROP_HEIGHT_CM
  const size = propSizeFromHeightCm(heightCm)
  return {
    uid: nextObjectUid(objects),
    kind: "prop",
    shape: "box",
    entity_type: "prop",
    entity_id: prop.id,
    label: prop.name,
    color,
    size,
    position: nextDropPosition(objects, size, terrain, view),
    rotation: [0, 0, 0],
    scale: [1, 1, 1],
    missing: false,
    height_explicit: Boolean(prop.height_explicit),
    thumb_url: prop.thumb_url ?? prop.reference_sheet_url ?? null,
  }
}

function primitiveNominalSize(shape: ScenePrimitiveShape): [number, number, number] {
  return shape === "plane" ? [2, 0.04, 2] : [1, 1, 1]
}

/** Meter scenes keep the nominal size. A centimeter cell scales the figure to that cell. */
function primitiveSizeForPlatform(
  shape: ScenePrimitiveShape,
  platform?: ScenePlatform | null,
): [number, number, number] {
  const nominal = primitiveNominalSize(shape)
  if (!platform || platform.unit !== "cm") return nominal
  const sector = sectorSizeMeters(platform)
  return [nominal[0] * sector, nominal[1] * sector, nominal[2] * sector]
}

export function buildPrimitiveObject(
  shape: ScenePrimitiveShape,
  objects: SceneObjectState[],
  terrain?: DropTerrain | null,
  view?: DropViewHint | null,
): SceneObjectState {
  const size = primitiveSizeForPlatform(shape, terrain?.platform)
  return {
    uid: nextObjectUid(objects),
    kind: "primitive",
    shape,
    size,
    position: nextDropPosition(objects, size, terrain, view),
    rotation: [0, 0, 0],
    scale: [1, 1, 1],
  }
}

const FACE_COLOR_RE = /^#[0-9a-fA-F]{6}$/
const TILE_KEY_RE = /^\d+:\d+$/

/** `undefined` when `faces` was not loaded — omit the key so a save keeps stored colors. */
export function faceColorsFromFaces(
  faces: SceneFaceState[] | null | undefined,
): Record<string, string | Record<string, string>> | undefined {
  if (!faces) return undefined
  const out: Record<string, string | Record<string, string>> = {}
  for (const face of faces) {
    const tiles = face.tile_colors
    if (tiles && Object.keys(tiles).length > 0) {
      const tileMap: Record<string, string> = {}
      for (const [key, hex] of Object.entries(tiles)) {
        if (!TILE_KEY_RE.test(key) || !hex || !FACE_COLOR_RE.test(hex)) continue
        tileMap[key] = hex.toLowerCase()
      }
      if (Object.keys(tileMap).length > 0) {
        out[face.face_key] = tileMap
        continue
      }
    }
    const solid = face.solid_color?.trim()
    if (solid && FACE_COLOR_RE.test(solid)) out[face.face_key] = solid.toLowerCase()
  }
  return out
}

/** Full scene snapshot for previz persist (objects + camera + terrain). */
export function sceneStateForSave(state: SceneLayoutState): {
  objects: SceneObjectState[]
  camera_state: SceneCameraState
  start_camera: SceneCameraState | Record<string, never>
  camera_path: CameraPath
  camera_paths: CameraPathTake[]
  active_camera_path_id: string | null
  platform: SceneLayoutState["platform"]
  cells: NonNullable<SceneLayoutState["cells"]>
  openings: NonNullable<SceneLayoutState["openings"]>
  light: SceneLayoutState["light"]
  environment: SceneEnvironmentState
  face_colors?: Record<string, string | Record<string, string>>
} {
  const faceColors = faceColorsFromFaces(state.faces)
  return {
    objects: state.objects.map((obj) => ({
      uid: obj.uid,
      kind: obj.kind,
      shape: obj.shape,
      entity_type: obj.entity_type,
      entity_id: obj.entity_id,
      label: obj.label,
      ...(obj.group_id ? { group_id: obj.group_id } : {}),
      color: obj.color,
      ...(obj.kind === "character" && obj.figure ? { figure: obj.figure } : {}),
      size: obj.size,
      position: obj.position,
      rotation: obj.rotation,
      scale: obj.scale,
      ...(obj.path ? { path: cloneObjectPath(obj.path) } : {}),
    })),
    camera_state: state.camera_state,
    start_camera: state.start_camera ?? {},
    camera_path: state.camera_path,
    camera_paths: state.camera_paths ?? [],
    active_camera_path_id: state.active_camera_path_id ?? null,
    platform: state.platform ?? null,
    cells: state.cells ?? [],
    openings: state.openings ?? [],
    light: state.light ?? null,
    environment: normalizeSceneEnvironment(state.environment),
    ...(faceColors !== undefined ? { face_colors: faceColors } : {}),
  }
}

export type SceneStateSavePayload = ReturnType<typeof sceneStateForSave>
